import {
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import env from '../config/env';
import { hasEntraPassword } from './entraPassword';
import { safeReturnPath } from './returnPath';

const INTENT_KEY = 'mashlom.entra.intent';
// A legacy session moving to Microsoft keeps its token here until the account is
// linked, and gets it back if linking is refused (docs/EntraFullMigrationPlan.md D21).
const MOVE_TOKEN_KEY = 'mashlom.entra.moveToken';
const MOVE_ERROR_KEY = 'mashlom.entra.moveError';
// What the sign-in after "sign in with another account" should do again: complete
// the same invitation, and return to the same page. That button signs out of
// Microsoft first, which drops the intent, and Microsoft comes back to /signin.
const NEXT_SIGN_IN_KEY = 'mashlom.entra.nextSignIn';
// Where MSAL marks a redirect or popup as under way (its temporary cache).
const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';
const HEBREW_AUTH_QUERY_PARAMETERS = {
  ui_locales: 'he-IL',
  mkt: 'he-IL',
};

let runtime = {
  capabilities: { enabled: false, mode: 'disabled' },
  instance: null,
  redirectResult: null,
  cachedAccessToken: null,
  error: null,
};
let initializationPromise = null;
let forceRefresh = false;

const callbackUri = () => `${window.location.origin}/auth/callback`;
// The page Microsoft returns to from a silent request (hidden iframe) or a popup:
// a small page that only passes the answer back to MSAL (auth/redirectBridge.js).
// Returning to /auth/callback would boot the whole app inside the iframe and the
// request would fail. Registered as an SPA redirect URI.
const blankPageUri = () => `${window.location.origin}/auth-popup.html`;
// Where Microsoft returns after sign-out: its own sign-in page, through /signin.
// Microsoft's session has ended by then, so it asks again instead of signing the
// same user back in. Registered as an SPA redirect URI.
const logoutUri = () => `${window.location.origin}/signin`;

// Every trip to Microsoft (sign-in, sign-up, MFA, renewal, sign-out) replaces
// the page that started it, so Back from Microsoft does not land on a page that
// would only send the user there again. A move is the exception: it starts from
// a page the user was working in, still signed in, and Back from Microsoft
// returns there (settleMove). Back after the return is handled by
// auth/useEntraBackGuard.
let keepPageInHistory = false;
const onRedirectNavigate = (url) => {
  if (keepPageInHistory) {
    keepPageInHistory = false;
    return true;
  }
  window.location.replace(url);
  return false;
};

const readIntent = () => {
  try {
    return JSON.parse(sessionStorage.getItem(INTENT_KEY) || 'null');
  } catch {
    return null;
  }
};

// A move keeps the legacy session (localStorage `token`) while the user is on
// Microsoft's pages, so leaving them any other way than a finished sign-in (Back,
// closing the window, an error from Microsoft) finds the user where they were,
// still signed in. Only a sign-in that came back hands over: the token is taken
// out here, before UserContext reads it, and the move continues with the copy in
// MOVE_TOKEN_KEY, which UserContext restores if the link is refused (D21).
// Returns the message to show when Microsoft answered with an error.
const settleMove = ({ signedIn, errorMessage }) => {
  if (readIntent()?.intent !== 'migrate') return null;
  if (signedIn) {
    localStorage.removeItem('token');
    return null;
  }
  sessionStorage.removeItem(INTENT_KEY);
  sessionStorage.removeItem(MOVE_TOKEN_KEY);
  if (errorMessage) setMoveError(errorMessage);
  return errorMessage;
};

const initialize = async () => {
  try {
    const response = await fetch(`${env.API_BASE_URL || ''}/api/auth/capabilities`, {
      cache: 'no-store',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to load authentication capabilities');
    const capabilities = (await response.json()).entra || { enabled: false, mode: 'disabled' };
    if (!capabilities.enabled || !capabilities.msal) {
      runtime = { capabilities: { enabled: false, mode: 'disabled' }, instance: null, redirectResult: null, cachedAccessToken: null, error: null };
      return runtime;
    }

    const authorityHost = new URL(capabilities.msal.authority).host;
    const instance = new PublicClientApplication({
      auth: {
        clientId: capabilities.msal.clientId,
        authority: capabilities.msal.authority,
        knownAuthorities: [authorityHost],
        redirectUri: callbackUri(),
        postLogoutRedirectUri: logoutUri(),
        navigateToLoginRequestUrl: false,
        onRedirectNavigate,
      },
      cache: {
        // Share the Microsoft session across tabs on the same origin. MSAL
        // still owns these entries; application code never reads token values.
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      // Development only: MSAL explains why a silent token request failed.
      system: import.meta.env.DEV ? {
        loggerOptions: {
          logLevel: LogLevel.Info,
          piiLoggingEnabled: false,
          loggerCallback: (level, message, containsPii) => {
            if (!containsPii) console.debug(`[MSAL] ${message}`);
          },
        },
      } : undefined,
    });
    await instance.initialize();
    // Microsoft can answer the sign-in with an error, e.g. its remembered session
    // belongs to a user deleted from the tenant (AADSTS16000). Entra stays
    // enabled, so the callback page can sign out of Microsoft and let the user
    // pick another account instead of failing the same way again.
    let redirectResult = null;
    let redirectError = null;
    try {
      redirectResult = await instance.handleRedirectPromise();
    } catch (error) {
      console.error('Microsoft returned an error to the sign-in:', error);
      redirectError = redirectErrorMessage(error);
      // Any account still cached is from before this failed sign-in and cannot
      // get a token, so the app must not treat it as signed in. Local only; the
      // callback page's "another account" button ends Microsoft's session.
      await instance.clearCache();
    }
    // An unfinished or failed move leaves the user in the legacy session; its
    // error is shown by the migration popup there, not on the callback page.
    if (settleMove({ signedIn: Boolean(redirectResult?.account), errorMessage: redirectError })) {
      redirectError = null;
    }
    const account = redirectResult?.account || instance.getAllAccounts()[0] || null;
    if (account) instance.setActiveAccount(account);
    runtime = { capabilities, instance, redirectResult, cachedAccessToken: redirectResult?.accessToken || null, error: redirectError };
    // Back from Microsoft's two-step verification: use that token from now on.
    if (redirectResult?.accessToken && capabilities.mfaContext && provesMfa(redirectResult.accessToken)) {
      keepMfaToken(redirectResult.accessToken);
    }
    return runtime;
  } catch (error) {
    console.error('Entra initialization failed; continuing with legacy authentication:', error);
    const isRedirectReturn = window.location.pathname === '/auth/callback'
      || Boolean(sessionStorage.getItem(INTENT_KEY));
    runtime = {
      capabilities: { enabled: false, mode: 'disabled' },
      instance: null,
      redirectResult: null,
      cachedAccessToken: null,
      error: isRedirectReturn ? 'ההתחברות באמצעות Microsoft נכשלה. יש לנסות שוב.' : null,
    };
    return runtime;
  }
};

// AADSTS16000: Microsoft's remembered session points to a user that no longer
// exists in the tenant (or never signed up through this app).
const redirectErrorMessage = (error) => (
  String(error?.errorMessage || error?.message || '').includes('AADSTS16000')
    ? 'החשבון שאיתו ניסית להתחבר אינו קיים במערכת. יש להתחבר עם חשבון אחר.'
    : 'ההתחברות באמצעות Microsoft נכשלה. יש לנסות שוב, או להתחבר עם חשבון אחר.'
);

export const initializeRuntimeAuth = () => {
  if (!initializationPromise) initializationPromise = initialize();
  return initializationPromise;
};

export const getAuthRuntime = () => runtime;

// After Microsoft answered a sign-in with an error, or Mashlom refused the
// session, there is no account in the cache, but Microsoft's session must still
// be ended for another account to work.
export const hasEntraSignInError = () => Boolean(
  runtime.instance && (runtime.error || runtime.sessionRejected)
);

export const hasEntraAccount = () => Boolean(
  runtime.instance && (runtime.instance.getActiveAccount() || runtime.instance.getAllAccounts()[0])
);

// The account to name to Microsoft in an interactive request. MSAL names it by
// the ID token's login_hint claim or its user name, and both point at the
// tenant's internal <objectId>@<tenant>.onmicrosoft.com. For a Google
// (federated) user, Microsoft then asks for a password the account does not
// have. Such a request names no account, so Microsoft uses its own session or
// shows its sign-in page. MSAL would fall back to the active account's hint, so
// that is cleared too: the page is leaving, and initialize() sets it again.
const interactiveAccount = (account) => {
  if (!account || hasEntraPassword(account.idTokenClaims)) return account;
  runtime.instance.setActiveAccount(null);
  return undefined;
};

// MSAL's silent renewal names the account the same way, and Microsoft cannot
// match that name to a Google user's session (AADSTS50058). Asked with no hint,
// Microsoft takes the user from its own session. Another user signed in there is
// not this session's, so that answer is ignored and the caller goes interactive.
// Requests that need a token at the same moment share one attempt.
let pendingHintlessRenewal = null;
const renewWithoutHint = (account) => {
  if (hasEntraPassword(account.idTokenClaims)) return Promise.resolve(null);
  if (!pendingHintlessRenewal) {
    pendingHintlessRenewal = ssoSilentWithoutHint(account)
      .finally(() => { pendingHintlessRenewal = null; });
  }
  return pendingHintlessRenewal;
};
const ssoSilentWithoutHint = async (account) => {
  // ssoSilent falls back to the active account's hint as well.
  runtime.instance.setActiveAccount(null);
  try {
    const result = await runtime.instance.ssoSilent({
      scopes: [runtime.capabilities.msal.apiScope],
      redirectUri: blankPageUri(),
    });
    return result.account?.homeAccountId === account.homeAccountId ? result : null;
  } catch (error) {
    console.warn('Renewing the Microsoft session without a hint failed:', error?.errorCode || error?.name);
    return null;
  } finally {
    runtime.instance.setActiveAccount(account);
  }
};

export const getEntraAccessToken = async () => {
  if (!runtime.instance || !runtime.capabilities?.msal?.apiScope) return null;
  if (mfaRequired) return acquireMfaToken();
  const account = runtime.instance.getActiveAccount() || runtime.instance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await runtime.instance.acquireTokenSilent({
      account,
      scopes: [runtime.capabilities.msal.apiScope],
      forceRefresh,
      redirectUri: blankPageUri(),
    });
    forceRefresh = false;
    runtime.cachedAccessToken = result.accessToken;
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const renewed = await renewWithoutHint(account);
      if (renewed) {
        forceRefresh = false;
        runtime.cachedAccessToken = renewed.accessToken;
        return renewed.accessToken;
      }
      if (!sessionStorage.getItem(INTENT_KEY)) {
        sessionStorage.setItem(INTENT_KEY, JSON.stringify({ intent: 'login', invitationToken: null }));
      }
      await runtime.instance.acquireTokenRedirect({
        account: interactiveAccount(account),
        scopes: [runtime.capabilities.msal.apiScope],
        redirectUri: callbackUri(),
        extraQueryParameters: HEBREW_AUTH_QUERY_PARAMETERS,
      });
      return null;
    }
    throw error;
  }
};

// False for a Google (federated) sign-in: there is no Entra password to change.
export const canChangeEntraPassword = () => {
  const account = runtime.instance?.getActiveAccount() || runtime.instance?.getAllAccounts()[0];
  return Boolean(account) && hasEntraPassword(account.idTokenClaims);
};

// Entra users change their own password through Microsoft Graph. The browser
// sends it straight to Microsoft; it never reaches the Mashlom API. The SPA
// registration needs the delegated User-PasswordProfile.ReadWrite.All permission
// with admin consent, and /auth-popup.html as an SPA redirect URI: the popup lands
// on that blank page so the app does not boot inside it.
const GRAPH_PASSWORD_SCOPE = 'https://graph.microsoft.com/User-PasswordProfile.ReadWrite.All';

export const changeEntraPasswordViaGraph = async (currentPassword, newPassword) => {
  if (!runtime.instance) throw new Error('Microsoft authentication is not available');
  const account = runtime.instance.getActiveAccount() || runtime.instance.getAllAccounts()[0];
  if (!account) throw new Error('No Microsoft account is signed in');
  const request = { account, scopes: [GRAPH_PASSWORD_SCOPE] };
  let result;
  try {
    result = await runtime.instance.acquireTokenSilent({ ...request, redirectUri: blankPageUri() });
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error;
    result = await runtime.instance.acquireTokenPopup({
      ...request,
      redirectUri: blankPageUri(),
      extraQueryParameters: HEBREW_AUTH_QUERY_PARAMETERS,
    });
  }
  const response = await fetch('https://graph.microsoft.com/v1.0/me/changePassword', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${result.accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (response.status === 204) return { ok: true };
  let body = null;
  try { body = await response.json(); } catch { /* empty body */ }
  return { ok: false, status: response.status, error: body?.error || null };
};

// Two-step verification. The server publishes the Entra authentication context
// (capabilities.mfaContext, e.g. 'c1') whose Conditional Access policy requires
// MFA; asking for it puts that context in the token's `acrs` claim, which the
// server checks. See the "Two-step verification" section of docs/Authentication.md.
export const isMfaAvailable = () => Boolean(runtime.capabilities?.mfaContext);

// Microsoft could not supply a token proving MFA even after its own page, which
// means the tenant's Conditional Access setup is wrong. Retrying would loop.
export class MfaUnavailableError extends Error {
  constructor() {
    super('Two-step verification could not be completed');
    this.name = 'MfaUnavailableError';
  }
}

const MFA_ATTEMPT_KEY = 'mashlom.entra.mfaAttempt';
const MFA_ATTEMPT_WINDOW_MS = 2 * 60 * 1000;

const mfaClaims = () => JSON.stringify({
  access_token: { acrs: { essential: true, value: runtime.capabilities.mfaContext } },
});

const tokenPayload = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part + '='.repeat((4 - (part.length % 4)) % 4)));
  } catch {
    return null;
  }
};

const provesMfa = (token) => {
  const acrs = tokenPayload(token)?.acrs;
  return Array.isArray(acrs) && acrs.includes(runtime.capabilities.mfaContext);
};

const isFresh = (token) => {
  const exp = tokenPayload(token)?.exp;
  return Boolean(exp) && exp * 1000 - Date.now() > 60 * 1000;
};

// MSAL keeps a token obtained with the MFA claims apart from plain ones, so a
// plain request would get the old token without the context back. Once MFA was
// needed in this page, every request uses this token (getEntraAccessToken).
let mfaRequired = false;
let mfaToken = null;

const keepMfaToken = (token) => {
  mfaRequired = true;
  mfaToken = token;
  forceRefresh = false;
  runtime.cachedAccessToken = token;
  sessionStorage.removeItem(MFA_ATTEMPT_KEY);
};

const recentlyAttempted = () => {
  const at = Number(sessionStorage.getItem(MFA_ATTEMPT_KEY));
  return Boolean(at) && Date.now() - at < MFA_ATTEMPT_WINDOW_MS;
};

const clearMfaState = () => {
  mfaRequired = false;
  mfaToken = null;
  sessionStorage.removeItem(MFA_ATTEMPT_KEY);
};

const currentPath = () => `${window.location.pathname}${window.location.search}`;

let pendingMfaRequest = null;

const requestMfaToken = async (returnTo) => {
  if (!runtime.instance || !isMfaAvailable()) return null;
  const account = runtime.instance.getActiveAccount() || runtime.instance.getAllAccounts()[0];
  if (!account) return null;
  mfaRequired = true;
  if (mfaToken && isFresh(mfaToken)) return mfaToken;

  const request = { account, scopes: [runtime.capabilities.msal.apiScope], claims: mfaClaims() };
  try {
    // Succeeds without a prompt when the Microsoft session already passed MFA.
    const result = await runtime.instance.acquireTokenSilent({ ...request, redirectUri: blankPageUri() });
    if (provesMfa(result.accessToken)) {
      keepMfaToken(result.accessToken);
      return result.accessToken;
    }
  } catch {
    // Falls through: Microsoft has to ask.
  }
  // Back from Microsoft's page within the last minutes, still without the
  // context: stop instead of sending the user round in circles.
  if (recentlyAttempted()) throw new MfaUnavailableError();
  sessionStorage.setItem(MFA_ATTEMPT_KEY, String(Date.now()));
  // Leave for Microsoft's code page; EntraCallback brings the user back to `returnTo`.
  sessionStorage.setItem(INTENT_KEY, JSON.stringify({ intent: 'mfa', invitationToken: null, returnTo }));
  await runtime.instance.acquireTokenRedirect({
    ...request,
    account: interactiveAccount(request.account),
    redirectUri: callbackUri(),
    extraQueryParameters: HEBREW_AUTH_QUERY_PARAMETERS,
  });
  return null;
};

// Resolves with a token that proved MFA, or with null while the page leaves for
// Microsoft; throws MfaUnavailableError when Microsoft cannot supply one.
// Concurrent callers (several requests refused at once) share one attempt.
export const acquireMfaToken = ({ returnTo = currentPath() } = {}) => {
  if (!pendingMfaRequest) {
    pendingMfaRequest = requestMfaToken(returnTo).finally(() => {
      pendingMfaRequest = null;
    });
  }
  return pendingMfaRequest;
};

export const getBearerToken = async () => {
  const legacyToken = localStorage.getItem('token');
  if (legacyToken) return legacyToken;
  return getEntraAccessToken();
};

export const getCachedBearerToken = () => (
  localStorage.getItem('token') || runtime.cachedAccessToken || null
);

export const invalidateEntraAccessToken = () => {
  runtime.cachedAccessToken = null;
  mfaToken = null;
  forceRefresh = true;
};

export const beginEntraLogin = async ({
  intent = 'login',
  invitationToken = null,
  loginHint = null,
  // 'create' opens Microsoft's sign-up page instead of the sign-in page.
  prompt = null,
  // Where to go after the callback (EntraCallback); kept only if it is safe.
  returnTo = null,
} = {}) => {
  if (!runtime.instance || !runtime.capabilities.enabled) {
    throw new Error('Microsoft authentication is not available');
  }
  const legacyToken = localStorage.getItem('token');
  const moving = intent === 'migrate' && Boolean(legacyToken);
  if (moving) {
    // The legacy session stays signed in until Microsoft answers (settleMove).
    sessionStorage.setItem(MOVE_TOKEN_KEY, legacyToken);
  } else {
    sessionStorage.removeItem(MOVE_TOKEN_KEY);
    localStorage.removeItem('token');
  }
  keepPageInHistory = moving;
  sessionStorage.setItem(INTENT_KEY, JSON.stringify({ intent, invitationToken, returnTo: safeReturnPath(returnTo) }));
  sessionStorage.removeItem(NEXT_SIGN_IN_KEY);
  // MSAL refuses to start while it thinks another redirect is under way. By now
  // initialize() has handled any answer from Microsoft, so a flag still set
  // belongs to a redirect that never left the page (e.g. a token renewal cut
  // short), and would block every sign-in in this tab.
  sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  // Without an account in the request MSAL still sends the active account's
  // login_hint. For a Google user Microsoft then asks for a password the account
  // does not have, so a new sign-in starts with no account in mind.
  runtime.instance.setActiveAccount(null);
  await runtime.instance.loginRedirect({
    scopes: ['openid', 'profile', 'email', runtime.capabilities.msal.apiScope],
    redirectUri: callbackUri(),
    ...(loginHint ? { loginHint } : {}),
    ...(prompt ? { prompt } : {}),
    extraQueryParameters: HEBREW_AUTH_QUERY_PARAMETERS,
  });
};

export const consumeEntraIntent = () => {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    sessionStorage.removeItem(INTENT_KEY);
    return raw ? JSON.parse(raw) : { intent: 'login', invitationToken: null };
  } catch {
    sessionStorage.removeItem(INTENT_KEY);
    return { intent: 'login', invitationToken: null };
  }
};

export const clearEntraIntent = () => {
  sessionStorage.removeItem(INTENT_KEY);
};

// The intent of a sign-in still under way, without consuming it.
export const peekEntraIntent = () => {
  try {
    return JSON.parse(sessionStorage.getItem(INTENT_KEY)) || null;
  } catch {
    return null;
  }
};

// Read by /signin when it starts; removed by the next beginEntraLogin.
export const readNextSignIn = () => {
  try {
    return JSON.parse(sessionStorage.getItem(NEXT_SIGN_IN_KEY)) || null;
  } catch {
    return null;
  }
};

export const clearNextSignIn = () => sessionStorage.removeItem(NEXT_SIGN_IN_KEY);

// How the current Microsoft session signed in ('local', 'google.com', …). Only
// the ID token says so; the access token the server checks has no idp claim.
export const signInIdp = () => {
  const account = runtime.instance?.getActiveAccount() || runtime.instance?.getAllAccounts()[0];
  const claims = account?.idTokenClaims;
  if (!claims) return null;
  return hasEntraPassword(claims) ? 'local' : claims.idp;
};

// The legacy token kept aside by a move (beginEntraLogin with intent 'migrate').
export const getMoveToken = () => sessionStorage.getItem(MOVE_TOKEN_KEY);

export const clearMoveToken = () => sessionStorage.removeItem(MOVE_TOKEN_KEY);

// Why the last move was refused, shown once by the migration dialog.
export const setMoveError = (message) => sessionStorage.setItem(MOVE_ERROR_KEY, message);

export const takeMoveError = () => {
  const message = sessionStorage.getItem(MOVE_ERROR_KEY);
  sessionStorage.removeItem(MOVE_ERROR_KEY);
  return message;
};

// Mashlom refused this Microsoft session (e.g. the email belongs to a password
// account). Forget it locally, so no request renews it: a renewal can redirect to
// Microsoft, which signs the same account straight back in and is refused again.
export const rejectEntraSession = async () => {
  if (!runtime.instance) return;
  runtime.sessionRejected = true;
  await forgetEntraAccounts();
};

// Drops any Microsoft account from this browser without telling Microsoft. A
// password or legacy Google sign-in and sign-out use it, so an account left
// from an earlier Microsoft sign-in never stands in for the session.
export const forgetEntraAccounts = async () => {
  if (!runtime.instance) return;
  runtime.cachedAccessToken = null;
  clearMfaState();
  await runtime.instance.clearCache();
};

// `nextSignIn` ({ invitationToken, returnTo }) is what the sign-in that follows
// on /signin repeats; a plain sign-out keeps nothing.
export const logoutEntra = async ({ nextSignIn = null } = {}) => {
  clearMfaState();
  runtime.cachedAccessToken = null;
  forceRefresh = false;
  sessionStorage.removeItem(INTENT_KEY);
  const next = {
    invitationToken: nextSignIn?.invitationToken || null,
    returnTo: safeReturnPath(nextSignIn?.returnTo),
  };
  if (next.invitationToken || next.returnTo) {
    sessionStorage.setItem(NEXT_SIGN_IN_KEY, JSON.stringify(next));
  } else {
    sessionStorage.removeItem(NEXT_SIGN_IN_KEY);
  }
  if (!runtime.instance) return;
  const account = runtime.instance.getActiveAccount() || runtime.instance.getAllAccounts()[0];
  await runtime.instance.logoutRedirect({
    account,
    postLogoutRedirectUri: logoutUri(),
    extraQueryParameters: HEBREW_AUTH_QUERY_PARAMETERS,
  });
};
